import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoCarrierName } from "@redotech/redo-fulfillment-rate-common/carrier-account";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { NotificationBannerItem } from "@redotech/redo-web/arbiter-components/notification/redo-notification-banner";
import { useContext } from "react";
import { useLocation } from "react-router-dom";
import { ActionRequiredSeverity } from "./action-required-severity";

export function useFedexMfaBanner(): {
  severity: ActionRequiredSeverity;
  item: NotificationBannerItem;
} | null {
  const team = useContext(TeamContext);
  const location = useLocation();
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const shouldCheck =
    !!team?.settings?.outboundLabels?.newOmsEnabled &&
    (location.pathname.includes("/outbound-labels") ||
      location.pathname.includes("/settings/oms") ||
      location.pathname.includes("/settings/carriers"));

  const carrierAccountsLoad = useLoad(
    async (signal) => {
      if (!shouldCheck) {
        return [];
      }
      const result = await rpcClient.getCarrierAccounts(null, { signal });
      return result.merchantCarrierAccounts ?? [];
    },
    [rpcClient, shouldCheck, team?._id],
  );

  if (
    !shouldCheck ||
    carrierAccountsLoad.pending ||
    carrierAccountsLoad.error
  ) {
    return null;
  }

  const needsMfa = carrierAccountsLoad.value?.some(
    (account) =>
      (account.redoCarrierName === RedoCarrierName.FEDEX ||
        account.redoCarrierName === RedoCarrierName.FEDEX_SMARTPOST) &&
      "mfaComplete" in account &&
      !account.mfaComplete,
  );

  if (!needsMfa) {
    return null;
  }

  return {
    severity: ActionRequiredSeverity.High,
    item: {
      id: "fedex-mfa-migration",
      type: "error",
      bgColor: "error-secondary",
      borderColor: "error-solid",
      message:
        "FedEx requires multi-factor authentication (MFA). Complete MFA by October 30, 2026 to avoid interruptions to shipping rates and label purchases.",
      actions: [
        {
          actionType: "link",
          text: "Enable MFA",
          href: "/settings/carriers?carriersTab=merchantCarriers",
          internal: true,
          target: "_self",
        },
      ],
    },
  };
}
